import React, { Fragment, useContext } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { ConnectAddon } from '@confluence/connect-addon';
import { ContentUnifiedQuery, ContentBlogUnifiedQuery } from '@confluence/content-unified-query';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import {
	usePageContentId,
	usePageSpaceKey,
	useContentType,
	usePageState,
} from '@confluence/page-context';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes';
import { ContentScreenBackgroundLayer } from '@confluence/content-container/entry-points/ContentScreenBackgroundLayer';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ContentViewedEvent } from '@confluence/content-body/entry-points/ContentViewedEvent';

import { useExperimentalThemeSpaceHomeOverrideQuery } from './useExperimentalThemeSpaceHomeOverrideQuery';

const connectAddonContainerStyle = { position: 'relative' };

const connectAddonProps = {
	contentStyle: {
		width: '100%',
		textAlign: 'center',
		display: 'inline-block',
	},
	width: '100%',
	height: '100%',
};

const SPACE_THEME_KEY_MODULE_KEY_REGEX = /^ac:(.*)__(.*)-remote-theme$/;
export const parseThemeKey = (themeKey: string) => {
	const regexResult = SPACE_THEME_KEY_MODULE_KEY_REGEX.exec(themeKey);
	return regexResult ? [regexResult[1], regexResult[2]] : [null, null];
};

export const SpaceBaseThemedComponent = ({ children }: React.PropsWithChildren<{}>) => {
	const { match } = useContext(RoutesContext);
	const [{ contentIdLoading }] = usePageState();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const [contentType] = useContentType();

	const chooseContentUnifiedQuery =
		contentType === 'blogpost' ? ContentBlogUnifiedQuery : ContentUnifiedQuery;

	const isSpaceOverview = match?.name === SPACE_OVERVIEW.name;
	const skipContentQuery = !contentId || !spaceKey || !isSpaceOverview || contentIdLoading;
	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const contentQueryResult = useQuery(chooseContentUnifiedQuery, {
		context: { single: true },
		variables: {
			contentId,
			spaceKey,
			versionOverride: null,
			useNewContentTopper: true,
		},
		skip: skipContentQuery,
	});

	const themeKey = contentQueryResult?.data?.space?.theme?.themeKey;
	const routeOverrideEnabled = contentQueryResult?.data?.space?.settings?.routeOverrideEnabled;
	const [themeAddonKey, themeModuleKey] = parseThemeKey(themeKey);
	const skipOverrideQuery = !themeAddonKey || !themeModuleKey || !routeOverrideEnabled;

	const overrideQueryResult = useExperimentalThemeSpaceHomeOverrideQuery({
		variables: {
			themeAddonKey,
			themeModuleKey,
			spaceKey: spaceKey || '',
			spaceHomePageId: contentId || '',
		},
		skip: skipOverrideQuery,
	});

	if (contentQueryResult.error) {
		getMonitoringClient().submitError(contentQueryResult.error, {
			attribution: Attribution.ECOSYSTEM,
		});
		markErrorAsHandled(contentQueryResult.error);
		return <Fragment>{children}</Fragment>;
	}

	if (overrideQueryResult.error) {
		getMonitoringClient().submitError(overrideQueryResult.error, {
			attribution: Attribution.ECOSYSTEM,
		});
		markErrorAsHandled(overrideQueryResult.error);
		return <Fragment>{children}</Fragment>;
	}

	if (contentIdLoading) {
		return null;
	}

	if (skipContentQuery) {
		return <Fragment>{children}</Fragment>;
	}

	if (contentQueryResult.loading || (!skipOverrideQuery && overrideQueryResult.loading)) {
		return null;
	}

	if (skipOverrideQuery) {
		return <Fragment>{children}</Fragment>;
	}

	if (!contentQueryResult.data || !overrideQueryResult.data) {
		return <Fragment>{children}</Fragment>;
	}

	const { hasSpaceHomeOverride, spaceHomeOverrideDescriptor } =
		overrideQueryResult.data.experimentalThemeSpaceHomeOverride;

	if (!hasSpaceHomeOverride || !spaceHomeOverrideDescriptor) {
		return <Fragment>{children}</Fragment>;
	}

	// Ensure required fields are present for ConnectAddonDescriptor
	if (
		!spaceHomeOverrideDescriptor.addon_key ||
		!spaceHomeOverrideDescriptor.key ||
		!spaceHomeOverrideDescriptor.url ||
		!spaceHomeOverrideDescriptor.contextJwt ||
		!spaceHomeOverrideDescriptor.structuredContext ||
		!spaceHomeOverrideDescriptor.productCtx
	) {
		return <Fragment>{children}</Fragment>;
	}

	const descriptor = {
		addon_key: spaceHomeOverrideDescriptor.addon_key,
		key: spaceHomeOverrideDescriptor.key,
		url: spaceHomeOverrideDescriptor.url,
		contextJwt: spaceHomeOverrideDescriptor.contextJwt,
		structuredContext: spaceHomeOverrideDescriptor.structuredContext,
		productContext: JSON.parse(spaceHomeOverrideDescriptor.productCtx),
	};

	return (
		<Fragment>
			<ContentScreenBackgroundLayer lookAndFeel={contentQueryResult?.data?.space?.lookAndFeel} />
			<ConnectAddon
				containerStyle={connectAddonContainerStyle}
				descriptor={descriptor}
				{...connectAddonProps}
			/>
			{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
			{!process.env.REACT_SSR && isSpaceOverview && <ContentViewedEvent contentId={contentId} />}
		</Fragment>
	);
};
